import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { usePresence } from '../utils/presence'
import { Button } from './ui/Button'
import { useI18n } from '../utils/i18n'

export function Modal({ open, onClose, title, children, footer }) {
  const presence = usePresence(open, { exitMs: 160 })
  const exiting = presence.phase === 'exit'
  const portalTarget = typeof document === 'undefined' ? null : document.body
  if (!presence.mounted) return null

  const node = (
    <div className={`fixed inset-0 z-[200] isolate flex items-center justify-center p-4 ${exiting ? 'pointer-events-none' : ''}`.trim()}>
      <div
        className={`absolute inset-0 bg-black/40 backdrop-blur-sm ${exiting ? 'chamii-overlay-exit' : 'chamii-overlay-enter'}`}
        onClick={onClose}
      />
      <div
        className={`relative bg-white dark:bg-slate-950 border-2 border-brand-textDark dark:border-slate-700 rounded-3xl shadow-[4px_4px_0px_0px_#E5BC2E] max-w-lg w-full mx-auto max-h-[90vh] overflow-hidden ${
          exiting ? 'chamii-pop-exit' : 'chamii-pop-enter'
        }`}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b-2 border-brand-textDark/10 dark:border-slate-800 bg-brand-bg dark:bg-slate-900">
          <h3 className="text-lg font-extrabold text-brand-textDark dark:text-slate-100">{title}</h3>
          <button
            onClick={onClose}
            className="btn-pushable p-2 rounded-2xl bg-white dark:bg-slate-950 text-brand-textDark dark:text-slate-100 border-2 border-brand-textDark dark:border-slate-700 shadow-[4px_4px_0px_0px_#CBD5E1] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all"
          >
            <X size={18} />
          </button>
        </div>
        <div className="px-5 py-4 overflow-y-auto max-h-[60vh]">{children}</div>
        {footer && (
          <div className="px-5 py-4 border-t-2 border-brand-textDark/10 dark:border-slate-800 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 bg-white dark:bg-slate-950">
            {footer}
          </div>
        )}
      </div>
    </div>
  )

  if (!portalTarget) return node
  return createPortal(node, portalTarget)
}

export function ConfirmModal({ open, onClose, onConfirm, title, message, confirmText, danger = false }) {
  const { tx } = useI18n()
  const defaultConfirmText = tx({ zh: '确认', en: 'Confirm' })
  const cancelText = tx({ zh: '取消', en: 'Cancel' })
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button onClick={onClose} variant="neutral">
            {cancelText}
          </Button>
          <Button
            onClick={() => {
              onConfirm()
              onClose()
            }}
            variant={danger ? 'danger' : 'secondary'}
          >
            {confirmText || defaultConfirmText}
          </Button>
        </>
      }
    >
      <p className="text-brand-textDark/80 dark:text-slate-200">{message}</p>
    </Modal>
  )
}

export function InputModal({ open, onClose, onSubmit, title, label, placeholder, defaultValue = '' }) {
  const { tx } = useI18n()
  const [value, setValue] = useState(defaultValue)

  useEffect(() => {
    if (open) setValue(defaultValue)
  }, [open, defaultValue])

  const handleSubmit = () => {
    if (value.trim()) {
      onSubmit(value.trim())
      onClose()
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button onClick={onClose} variant="neutral">
            {tx({ zh: '取消', en: 'Cancel' })}
          </Button>
          <Button onClick={handleSubmit} disabled={!value.trim()} variant="secondary">
            {tx({ zh: '确定', en: 'OK' })}
          </Button>
        </>
      }
    >
      <div>
        <label className="block text-sm text-brand-textDark/70 mb-2 font-extrabold">{label}</label>
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          className="w-full bg-white dark:bg-slate-950 border-2 border-brand-textDark/20 dark:border-slate-700 rounded-2xl px-4 py-2.5 text-brand-textDark dark:text-slate-100 outline-none focus:border-brand-cyan focus:ring-2 focus:ring-brand-cyan/20 transition-colors duration-150"
          autoFocus
          onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
        />
      </div>
    </Modal>
  )
}
